import { useTranslation } from '@/i18n'

interface Props {
  /** How many placeholder rows to draw — about a screen's worth. */
  rows?: number
}

/**
 * What a Tag mapping table shows while its FIRST rows are on the way.
 *
 * The page used to draw the column headers over an empty body with one small
 * spinner under them, for one to two seconds (review F10, "Lazy Load are
 * implemented but still 1-2 secs load"), which reads as an empty table that is
 * about to say "no tags". Six pulsing bars per row in the table's own six
 * columns say "rows are coming" instead, and hold the layout so nothing jumps
 * when they land. `aria-hidden`: the one screen-reader announcement is the
 * status line, not forty-eight bars.
 */
const TableSkeletonRows = ({ rows = 8 }: Props) => {
  const { t } = useTranslation()
  return (
    <>
      <tr className="sr-only" role="status" aria-live="polite">
        <td colSpan={6}>{t('data_catalog:tag_mapping.table.loading', { defaultValue: 'Loading tags…' })}</td>
      </tr>
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i} aria-hidden data-testid="table-skeleton-row" className="border-b border-border">
          {[24, 32, 56, 20, 12, 0].map((width, col) => (
            <td key={col} className={col === 5 ? 'px-2 py-3' : 'px-6 py-3'}>
              {width > 0 && (
                <div
                  className="h-4 animate-pulse rounded bg-muted"
                  style={{ width: `${width}%`, minWidth: '3rem' }}
                />
              )}
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

export default TableSkeletonRows
